import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api/axios';
import { Building2, Check, Globe, Package, Rocket, Sparkles, X, Leaf } from 'lucide-react';

// Separate PlanCard component to prevent unnecessary re-renders
const PlanCard = React.memo(({
  planKey,
  plan,
  index,
  planVisuals,
  pricingTranslations,
  language,
  billingInterval,
  currentPlan,
  currentBillingInterval,
  planHierarchy,
  onPlanSelect,
  isDark,
}) => {
  const t = pricingTranslations[language] || pricingTranslations.en;
  const cardRef = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, index * 150);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [index]);

  const visual = planVisuals[planKey] || planVisuals.free;
  const Icon = visual.Icon;

  return (
    <div
      ref={cardRef}
      className={[
        'group relative overflow-hidden rounded-xl border bg-white dark:bg-gray-800 p-6 shadow-sm dark:shadow-gray-900/50 transition-all duration-500',
        'hover:-translate-y-2 hover:shadow-2xl dark:hover:shadow-gray-900/70',
        plan.isPopular ? 'border-cyan-200 dark:border-cyan-800 ring-2 ring-cyan-500/40 dark:ring-cyan-500/20 hover:ring-cyan-500/60 dark:hover:ring-cyan-500/40' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-8',
      ].join(' ')}
    >
      {/* Animated background gradient on hover */}
      <div className={`absolute inset-0 bg-gradient-to-br ${visual.gradient} opacity-0 dark:opacity-0 group-hover:opacity-5 dark:group-hover:opacity-10 transition-opacity duration-500`}></div>

      {/* Glow effect for popular plan */}
      {plan.isPopular && (
        <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-400 dark:from-cyan-600 dark:via-blue-600 dark:to-cyan-600 rounded-xl opacity-0 group-hover:opacity-20 dark:group-hover:opacity-10 blur-xl transition-opacity duration-500"></div>
      )}
      {/* Visual header */}
      <div className={`relative -mx-6 -mt-6 mb-5 bg-gradient-to-br ${visual.gradient} p-5 overflow-hidden`}>
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.3),transparent_50%)]"></div>
        </div>

        <div className="relative flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${visual.iconWrap} group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-md group-hover:shadow-lg`}>
              <Icon className={`h-5 w-5 ${visual.accentText} group-hover:scale-110 transition-transform duration-500`} />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors">{plan.name}</h3>
            </div>
          </div>

          {plan.isPopular && (
            <span className="inline-flex items-center rounded-full bg-cyan-600 dark:bg-cyan-500 px-3 py-1 text-xs font-semibold text-white shadow-sm flex-shrink-0 group-hover:scale-105 group-hover:shadow-md transition-all duration-300 animate-pulse">
              {t.popular}
            </span>
          )}
        </div>
      </div>

      {/* Price */}
      <div className="mb-4 relative">
        {plan.price === '0' ? (
          <>
            <div className="text-3xl font-bold text-gray-900 dark:text-white group-hover:scale-105 transition-transform duration-300">
              0<span className="text-lg font-semibold text-gray-400 dark:text-gray-500"> €</span>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">{t.perMonth}</div>
          </>
        ) : plan.price === null ? (
          <div className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors">{t.contactUs}</div>
        ) : (
          <>
            <div className="text-3xl font-bold text-gray-900 dark:text-white group-hover:scale-105 transition-transform duration-300">
              €{plan.price}
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {billingInterval === 'year' ? t.perYear : t.perMonth}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Limits */}
      <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700 space-y-2 text-sm font-semibold text-gray-900 dark:text-white">
        <div className="flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-300">
          <Package className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-300" />
          <span>
            {plan.projects === Infinity ? t.unlimited : plan.projects} {t.projects}
          </span>
        </div>
        <div className="flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-300 delay-75">
          <Globe className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-300" />
          <span>
            {plan.domains === Infinity ? t.unlimited : plan.domains} {t.domains}
          </span>
        </div>
      </div>

      {/* Features */}
      <ul className="space-y-2 mb-6 text-sm text-gray-600 dark:text-gray-300">
        {plan.features.map((feature, idx) => {
          const FeatureIcon = feature.included ? Check : X;
          return (
            <li
              key={idx}
              className="flex items-start gap-2 group-hover:translate-x-1 transition-transform duration-300"
              style={{ transitionDelay: `${idx * 50}ms` }}
            >
              <FeatureIcon className={`mt-0.5 h-4 w-4 ${feature.included ? 'text-cyan-600 dark:text-cyan-400' : 'text-rose-500 dark:text-rose-400'} flex-shrink-0 group-hover:scale-110 transition-transform duration-300`} />
              <span className={feature.included ? 'group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors' : 'text-gray-500 dark:text-gray-400'}>{feature.text}</span>
            </li>
          );
        })}
      </ul>

      {/* Supported Frameworks */}
      <div className="relative mb-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">{t.supportedFrameworks}</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <img
            src={isDark ? "/entegrasyonIconLight/react_icon_light-64.webp" : "/entegrasyonIcon/react_icon-64.webp"}
            alt="React"
            loading="lazy"
            width="24"
            height="24"
            className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-all duration-300"
            title="React"
          />
          <img
            src={isDark ? "/entegrasyonIconLight/vue_icon_light-64.webp" : "/entegrasyonIcon/vue_icon-64.webp"}
            alt="Vue"
            loading="lazy"
            width="24"
            height="24"
            className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-all duration-300"
            title="Vue"
          />
          <img
            src={isDark ? "/entegrasyonIconLight/nuxt_icon_light-64.webp" : "/entegrasyonIcon/nuxt_icon-64.webp"}
            alt="Nuxt"
            loading="lazy"
            width="24"
            height="24"
            className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-all duration-300"
            title="Nuxt"
          />
          <img
            src={isDark ? "/entegrasyonIconLight/nextjs_icon_light-64.webp" : "/entegrasyonIcon/nextjs_icon-64.webp"}
            alt="Next.js"
            loading="lazy"
            width="24"
            height="24"
            className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-all duration-300"
            title="Next.js"
          />
          <img
            src={isDark ? "/entegrasyonIconLight/wordpress_icon_light-64.webp" : "/entegrasyonIcon/wordpress_icon-64.webp"}
            alt="WordPress"
            loading="lazy"
            width="24"
            height="24"
            className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-all duration-300"
            title="WordPress"
          />
          <img
            src={isDark ? "/entegrasyonIconLight/html_icon_light-64.webp" : "/entegrasyonIcon/html_icon-64.webp"}
            alt="HTML"
            loading="lazy"
            width="24"
            height="24"
            className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-all duration-300"
            title="HTML"
          />
        </div>
      </div>

      {/* Button */}
      {(() => {
        const isCurrentPlan = planKey === 'free'
          ? currentPlan === planKey
          : currentPlan === planKey && currentBillingInterval === billingInterval;
        let buttonText = t.getStarted;

        if (planKey === 'enterprise') {
          buttonText = t.contactUs;
        } else if (isCurrentPlan) {
          buttonText = `✓ ${t.currentPlan}`;
        } else if (currentPlan) {
          if (planKey === 'free' && currentPlan === 'free') {
            buttonText = `✓ ${t.currentPlan}`;
          }
          else if (currentPlan === planKey && currentBillingInterval !== billingInterval && planKey !== 'free') {
            if (billingInterval === 'year') {
              buttonText = t.switchToYearly;
            } else {
              buttonText = t.switchToMonthly;
            }
          } else if (planKey === 'free' && currentPlan !== 'free') {
            buttonText = t.downgrade;
          } else if (planKey !== 'free') {
            const currentPlanLevel = planHierarchy[currentPlan] || 0;
            const targetPlanLevel = planHierarchy[planKey] || 0;

            if (targetPlanLevel > currentPlanLevel) {
              buttonText = t.upgrade;
            } else if (targetPlanLevel < currentPlanLevel) {
              buttonText = t.downgrade;
            }
          }
        }

        return (
          <Button
            onClick={() => onPlanSelect && onPlanSelect(planKey)}
            variant={isCurrentPlan ? 'default' : 'outline'}
            disabled={isCurrentPlan}
            className="w-full group-hover:scale-105 hover:bg-cyan-600 hover:text-white hover:border-cyan-500 hover:shadow-lg hover:shadow-cyan-500/30 dark:border dark:border-gray-600 dark:hover:bg-cyan-600 dark:hover:text-white dark:hover:border-cyan-500 dark:hover:shadow-lg dark:hover:shadow-cyan-500/30 transition-all duration-300"
          >
            {buttonText}
          </Button>
        );
      })()}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom equality check - only re-render if these props change
  return (
    prevProps.plan === nextProps.plan &&
    prevProps.billingInterval === nextProps.billingInterval &&
    prevProps.currentPlan === nextProps.currentPlan &&
    prevProps.currentBillingInterval === nextProps.currentBillingInterval &&
    prevProps.onPlanSelect === nextProps.onPlanSelect &&
    prevProps.language === nextProps.language &&
    prevProps.isDark === nextProps.isDark
  );
});

PlanCard.displayName = 'PlanCard';

const pricingTranslations = {
  tr: {
    planFree: 'Ücretsiz',
    planStarter: 'Başlangıç',
    planProfessional: 'Profesyonel',
    planEnterprise: 'Kurumsal',
    featurePrioritySupport: 'Öncelikli destek',
    featureNoPrioritySupport: 'Öncelikli destek yok',
    featureNoAnalytics: 'Analitik verileri yok',
    featureAnalytics: 'Analitik verileri var',
    unlimited: 'Sınırsız',
    currentPlan: 'Mevcut Plan',
    getStarted: 'Hemen Başla',
    upgrade: 'Yükselt',
    downgrade: 'Düşür',
    switchToYearly: 'Yıllığa Geç',
    switchToMonthly: 'Aylığa Geç',
    contactUs: 'İletişime Geç',
    popular: 'POPÜLER',
    perMonth: '/ay',
    perYear: '/yıl',
    monthly: 'Aylık',
    yearly: 'Yıllık',
    yearlySavingsBadge: '2 ay tasarruf',
    projects: 'Proje',
    domains: 'Domain',
    features: 'Özellikler',
    supportedFrameworks: 'Desteklenen Framework\'ler',
  },
  en: {
    planFree: 'Free',
    planStarter: 'Starter',
    planProfessional: 'Professional',
    planEnterprise: 'Enterprise',
    featurePrioritySupport: 'Priority support',
    featureNoPrioritySupport: 'No priority support',
    featureNoAnalytics: 'No analytics',
    featureAnalytics: 'Analytics included',
    unlimited: 'Unlimited',
    currentPlan: 'Current Plan',
    getStarted: 'Get Started',
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    switchToYearly: 'Switch to Yearly',
    switchToMonthly: 'Switch to Monthly',
    contactUs: 'Contact Us',
    popular: 'POPULAR',
    perMonth: '/mo',
    perYear: '/year',
    monthly: 'Monthly',
    yearly: 'Yearly',
    yearlySavingsBadge: 'Save 2 months',
    projects: 'Projects',
    domains: 'Domains',
    features: 'Features',
    supportedFrameworks: 'Supported Frameworks',
  },
  de: {
    planFree: 'Kostenlos',
    planStarter: 'Starter',
    planProfessional: 'Professionell',
    planEnterprise: 'Enterprise',
    featurePrioritySupport: 'Prioritäts-Support',
    featureNoPrioritySupport: 'Kein Prioritäts-Support',
    featureNoAnalytics: 'Keine Analytics',
    featureAnalytics: 'Analytics enthalten',
    unlimited: 'Unbegrenzt',
    currentPlan: 'Aktueller Plan',
    getStarted: 'Jetzt starten',
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    switchToYearly: 'Auf Jährlich wechseln',
    switchToMonthly: 'Auf Monatlich wechseln',
    contactUs: 'Kontaktieren Sie uns',
    popular: 'BELIEBT',
    perMonth: '/Monat',
    perYear: '/Jahr',
    monthly: 'Monatlich',
    yearly: 'Jährlich',
    yearlySavingsBadge: '2 Monate sparen',
    projects: 'Projekte',
    domains: 'Domänen',
    features: 'Funktionen',
    supportedFrameworks: 'Unterstützte Frameworks',
  },
  es: {
    planFree: 'Gratis',
    planStarter: 'Starter',
    planProfessional: 'Profesional',
    planEnterprise: 'Empresarial',
    featurePrioritySupport: 'Soporte prioritario',
    featureNoPrioritySupport: 'Sin soporte prioritario',
    featureNoAnalytics: 'Sin analíticas',
    featureAnalytics: 'Analíticas incluidas',
    unlimited: 'Ilimitado',
    currentPlan: 'Plan Actual',
    getStarted: 'Comenzar',
    upgrade: 'Actualizar',
    downgrade: 'Reducir',
    switchToYearly: 'Cambiar a Anual',
    switchToMonthly: 'Cambiar a Mensual',
    contactUs: 'Contáctenos',
    popular: 'POPULAR',
    perMonth: '/mes',
    perYear: '/año',
    monthly: 'Mensual',
    yearly: 'Anual',
    yearlySavingsBadge: 'Ahorra 2 meses',
    projects: 'Proyectos',
    domains: 'Dominios',
    features: 'Características',
    supportedFrameworks: 'Frameworks Compatibles',
  },
  fr: {
    planFree: 'Gratuit',
    planStarter: 'Starter',
    planProfessional: 'Professionnel',
    planEnterprise: 'Entreprise',
    featurePrioritySupport: 'Support prioritaire',
    featureNoPrioritySupport: 'Pas de support prioritaire',
    featureNoAnalytics: "Pas d'analytique",
    featureAnalytics: 'Analytique incluse',
    unlimited: 'Illimité',
    currentPlan: 'Plan Actuel',
    getStarted: 'Commencer',
    upgrade: 'Mettre à niveau',
    downgrade: 'Réduire',
    switchToYearly: 'Passer à Annuel',
    switchToMonthly: 'Passer à Mensuel',
    contactUs: 'Nous Contacter',
    popular: 'POPULAIRE',
    perMonth: '/mois',
    perYear: '/an',
    monthly: 'Mensuel',
    yearly: 'Annuel',
    yearlySavingsBadge: 'Économisez 2 mois',
    projects: 'Projets',
    domains: 'Domaines',
    features: 'Caractéristiques',
    supportedFrameworks: 'Frameworks Supportés',
  },
};

const PricingPlansComponent = ({
  onPlanSelect,
  currentPlan = null,
  currentBillingInterval = null,
  showBillingToggle = true,
  billingInterval: externalBillingInterval = null,
  onBillingIntervalChange = null
}) => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = pricingTranslations[language] || pricingTranslations.en;
  const [internalBillingInterval, setInternalBillingInterval] = useState('month');

  // Plan hierarchy for determining upgrade/downgrade
  const planHierarchy = {
    free: 0,
    starter: 1,
    premium: 2,
    enterprise: 3,
  };

  // Use external billing interval if provided, otherwise use internal
  const billingInterval = externalBillingInterval !== null ? externalBillingInterval : internalBillingInterval;
  const setBillingInterval = (interval) => {
    if (onBillingIntervalChange) {
      onBillingIntervalChange(interval);
    } else {
      setInternalBillingInterval(interval);
    }
  };

  const [stripePlans, setStripePlans] = useState({});
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);

  const planVisuals = {
    free: {
      Icon: Leaf,
      gradient: 'from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900',
      accentText: 'text-slate-800 dark:text-slate-200',
      iconWrap: 'bg-white/70 dark:bg-gray-800/70 ring-1 ring-slate-200 dark:ring-slate-700',
    },
    starter: {
      Icon: Rocket,
      gradient: 'from-cyan-50 to-sky-100 dark:from-cyan-900/30 dark:to-sky-900/30',
      accentText: 'text-cyan-900 dark:text-cyan-200',
      iconWrap: 'bg-white/70 dark:bg-gray-800/70 ring-1 ring-cyan-200 dark:ring-cyan-800',
    },
    premium: {
      Icon: Sparkles,
      gradient: 'from-violet-50 to-fuchsia-100 dark:from-violet-900/30 dark:to-fuchsia-900/30',
      accentText: 'text-violet-900 dark:text-violet-200',
      iconWrap: 'bg-white/70 dark:bg-gray-800/70 ring-1 ring-violet-200 dark:ring-violet-800',
    },
    enterprise: {
      Icon: Building2,
      gradient: 'from-amber-50 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30',
      accentText: 'text-amber-900 dark:text-amber-200',
      iconWrap: 'bg-white/70 dark:bg-gray-800/70 ring-1 ring-amber-200 dark:ring-amber-800',
    },
  };

  // Memoize basePlans to update when language changes
  const basePlans = React.useMemo(() => ({
    free: {
      name: t.planFree,
      price: '0',
      projects: 1,
      domains: 1,
      features: [
        { text: t.featureNoPrioritySupport || 'No priority support', included: false },
        { text: t.featureNoAnalytics || 'No analytics', included: false },
      ],
      isPopular: false,
    },
    starter: {
      name: t.planStarter,
      price: '29',
      projects: 5,
      domains: 5,
      features: [
        { text: t.featurePrioritySupport, included: true },
        { text: t.featureAnalytics, included: true },
      ],
      isPopular: false,
    },
    premium: {
      name: t.planProfessional,
      price: '79',
      projects: 30,
      domains: 30,
      features: [
        { text: t.featurePrioritySupport, included: true },
        { text: t.featureAnalytics, included: true },
      ],
      isPopular: false,
    },
    enterprise: {
      name: t.planEnterprise,
      price: null,
      projects: Infinity,
      domains: Infinity,
      features: [
        { text: t.featurePrioritySupport, included: true },
        { text: t.featureAnalytics, included: true },
      ],
      isPopular: false,
    },
  }), [language, t]);

  // Fetch pricing from backend
  useEffect(() => {
    const fetchStripePlans = async () => {
      try {
        const response = await api.get('/stripe/plans');
        if (response.data) {
          setStripePlans(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch Stripe plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStripePlans();
  }, []);

  // Update displayed prices based on billing interval
  useEffect(() => {
    if (Object.keys(stripePlans).length === 0) {
      setPlans(basePlans);
      return;
    }

    const newPlans = { ...basePlans };

    Object.keys(stripePlans).forEach(planKey => {
      if (newPlans[planKey]) {
        const prices = stripePlans[planKey]?.prices || {};
        const selected = prices[billingInterval] || prices.month || prices.year;
        if (selected?.price !== undefined) {
          newPlans[planKey] = {
            ...newPlans[planKey],
            price: selected.price.toString(),
            currency: selected.currency
          };
        }
      }
    });

    setPlans(newPlans);
  }, [billingInterval, stripePlans, basePlans]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 dark:border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Billing Toggle */}
      {showBillingToggle && (
        <div className="flex items-center justify-center md:justify-end">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1">
            <button
              type="button"
              onClick={() => setBillingInterval('month')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                billingInterval === 'month'
                  ? 'bg-cyan-600 dark:bg-cyan-500 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {t.monthly}
            </button>
            <button
              type="button"
              onClick={() => setBillingInterval('year')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                billingInterval === 'year'
                  ? 'bg-cyan-600 dark:bg-cyan-500 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <span>{t.yearly}</span>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    billingInterval === 'year'
                      ? 'bg-white/15 text-white'
                      : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                  }`}
                >
                  {t.yearlySavingsBadge}
                </span>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.entries(plans).map(([planKey, plan], index) => (
          <PlanCard
            key={planKey}
            planKey={planKey}
            plan={plan}
            index={index}
            planVisuals={planVisuals}
            pricingTranslations={pricingTranslations}
            language={language}
            billingInterval={billingInterval}
            currentPlan={currentPlan}
            currentBillingInterval={currentBillingInterval}
            planHierarchy={planHierarchy}
            onPlanSelect={onPlanSelect}
            isDark={isDark}
          />
        ))}
      </div>
    </div>
  );
};

// Wrap PricingPlans with React.memo to prevent unnecessary re-renders
export const PricingPlans = React.memo(PricingPlansComponent, (prevProps, nextProps) => {
  // Custom equality check - only re-render if these props change
  return (
    prevProps.currentPlan === nextProps.currentPlan &&
    prevProps.currentBillingInterval === nextProps.currentBillingInterval &&
    prevProps.billingInterval === nextProps.billingInterval &&
    prevProps.onPlanSelect === nextProps.onPlanSelect &&
    prevProps.onBillingIntervalChange === nextProps.onBillingIntervalChange &&
    prevProps.showBillingToggle === nextProps.showBillingToggle
  );
});

PricingPlans.displayName = 'PricingPlans';
